import React, { useEffect, useState } from 'react'
import { SlotStatus, type ISlot } from '../../../models/DistributionPlan'
import type { ISyncRules } from '../../../models/SyncRules'
import GenericModal from '../../../components/GenericModal'
import { Box, Tab, Typography } from '@mui/material'
import { TabContext, TabList, TabPanel } from '@mui/lab'
import SurveyDatesPanel from './SurveyDatesPanel'
import SyncRulesPanel from './SyncRulesPanel'
import { green, grey } from '../../../colors'
import { useQuery } from '@tanstack/react-query'
import { type ISurveyDates } from '../../../models/AutoImports'
import { AutoImportManager } from '../../../APIManager'
import { InstructorAttributeAlert } from '../../SurveyFeature/styles'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'

interface SyncSectionModalProps {
  slot: ISlot
  courseSectionsModal: boolean
  setCourseSectionsModal: (arg: boolean) => void
  projectId: string
  isReadOnly: boolean
}
enum tabOptions {
  SURVEYDATES = '1',
  SYNCRULES = '2'
}

interface ModalTitleProps {
  tab: string
  isAutoImportActive: boolean
}

const ModalTitle: React.FC<ModalTitleProps> = ({ tab, isAutoImportActive }) => (
  <Box display='flex' flexDirection='column' alignItems='flex-start' mb={'8px'}>
    <Box display={'flex'} alignItems={'center'} justifyContent={'center'} gap={2}>
      <Typography fontSize={'18px'} fontWeight={600}>Sync Course Sections</Typography>
      {tab === tabOptions.SYNCRULES && (
        <>
          <Box borderRadius={'100%'} bgcolor={isAutoImportActive ? green[200] : grey[150]} width={'10px'} height={'10px'}></Box>
          <Typography fontSize={'14px'} color={isAutoImportActive ? green[200] : grey[150]}>{isAutoImportActive ? 'Sync On' : 'Sync Off'}</Typography>
        </>
      )}
    </Box>
    {tab === tabOptions.SURVEYDATES
      ? <Typography>Determine the dates the course sections should be synced to.</Typography>
      : <Typography>Determine which course sections should have their dates synced.</Typography>
    }
  </Box>
)

const SyncSectionModal: React.FC<SyncSectionModalProps> = ({ slot, courseSectionsModal, setCourseSectionsModal, projectId, isReadOnly }) => {
  const [tab, setTab] = useState<string>(tabOptions.SURVEYDATES)
  const [surveyDatesList, setSurveyDatesList] = useState<ISurveyDates[] | undefined>()
  const isAutoImportActive = slot.isCourseAttributeFilterActive || slot.isReportingHierarchyFilterActive || slot.isSyncAllSectionsForTermActive
  const status = slot?.slotNodeDetails?.slotStatus
  const isShowSyncCourseSectionBanner = status !== SlotStatus.Closed

  const handleChange = (event: React.SyntheticEvent, newValue: string): void => {
    setTab(newValue)
  }

  const handleCloseModal = (): void => {
    setCourseSectionsModal(false)
  }

  const setNextTab = (): void => {
    setTab(tabOptions.SYNCRULES)
  }

  const { data: surveyDatesData, isFetching, isLoading } = useQuery<ISurveyDates[]>({
    queryKey: ['dates', slot.slotId],
    queryFn: async (): Promise<ISurveyDates[]> => {
      const res = await AutoImportManager.getSurveyDates(slot.slotId)
      return res
    },
    enabled: !!slot?.slotId && courseSectionsModal
  })

  useEffect(() => {
    if (surveyDatesData) {
      setSurveyDatesList(surveyDatesData)
    }
  }, [surveyDatesData])

  const isDraftStatus = status === SlotStatus.Draft

  const { data: latestDefaultDates, isFetching: isFetchingLatestDefaultDates, isError: isErrorLatestDefaultDates, error: errorLatestDefaultDates } = useQuery<ISurveyDates[]>({
    queryKey: ['latestDefaultDates', projectId],
    retry: false,
    meta: { silent: true },
    queryFn: async (): Promise<ISurveyDates[]> => {
      const res = await AutoImportManager.getLatestSurveyDates(projectId)
      return res
    },
    enabled: !!projectId && projectId.trim().length > 0 && isDraftStatus
  })

  const { data: latestSyncRules, isFetching: isFetchingLatestSyncRules, isError: isErrorLatestSyncRules, error: errorLatestSyncRules } = useQuery<ISyncRules | null>({
    queryKey: ['latestSyncRules', projectId],
    retry: false,
    meta: { silent: true },
    queryFn: async (): Promise<ISyncRules | null> => {
      const res = await AutoImportManager.getLatestAutoImport(projectId)
      return res
    },
    enabled: !!projectId && projectId.trim().length > 0 && isDraftStatus
  })

  const { data: syncRulesData, isLoading: syncRulesLoading, isFetching: syncRulesFetching } = useQuery<ISyncRules>({
    queryKey: ['syncRulesData', slot.slotId],
    queryFn: async (): Promise<ISyncRules> => {
      const res = await AutoImportManager.getAutoImport(slot.slotId)
      return res
    },
    enabled: !!slot?.slotId && courseSectionsModal
  })

  return (
    <>
      <GenericModal
        isOpen={courseSectionsModal}
        title={<ModalTitle tab={tab} isAutoImportActive={isAutoImportActive} />}
        onClose={() => { setCourseSectionsModal(false) }}
        maxWidth='lg'
        action={false}
        showCloseIcon={false}
      >
        <>
          {
            isShowSyncCourseSectionBanner && isAutoImportActive &&
            <InstructorAttributeAlert severity="info" icon={<InfoOutlinedIcon sx={{ color: grey[950], fontWeight: 600 }} />}>
              Sync Course Section changes cannot be disabled once a survey project is in the live state.
            </InstructorAttributeAlert>
          }
          <Box mt={'8px'}>
            <TabContext value={tab} >
              <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <TabList onChange={handleChange} aria-label="tab">
                  <Tab label="Survey Dates" value={tabOptions.SURVEYDATES} />
                  <Tab label="Sync Rules" value={tabOptions.SYNCRULES} />
                </TabList>
              </Box>
              <TabPanel value={tabOptions.SURVEYDATES}>
                <SurveyDatesPanel
                  handleCloseModal={handleCloseModal}
                  slot={slot}
                  setNextTab={setNextTab}
                  surveyDatesList={surveyDatesList ?? []}
                  setSurveyDatesList={setSurveyDatesList}
                  loading={isFetching || isLoading}
                  isLatestDataFetching={isFetchingLatestDefaultDates || isErrorLatestDefaultDates || latestDefaultDates?.length === 0}
                  latestDefaultDates={latestDefaultDates ?? []}
                  // Propagate read-only mode so Draft+READ and non-Draft states cannot edit.
                  isReadOnly={isReadOnly}
                />
              </TabPanel>
              <TabPanel value={tabOptions.SYNCRULES}>
                <SyncRulesPanel
                  handleCloseModal={handleCloseModal}
                  slot={slot}
                  isLatestDataFetching={isFetchingLatestSyncRules || isErrorLatestSyncRules || !latestSyncRules}
                  latestSyncRules={latestSyncRules}
                  syncRulesData={syncRulesData}
                  loading={syncRulesLoading || syncRulesFetching}
                  isReadOnly={isReadOnly}
                />
              </TabPanel>
            </TabContext>
          </Box>
        </>
      </GenericModal>
    </>
  )
}

export default SyncSectionModal
